import { applyReliabilityMarkerCorrection } from './reliabilityMarkerCorrection';
import { reconcilePhaseTimelineClaims } from '../reportGeneration/phaseTimelineReconciliation';
import { applyWaterScreeningDisclosure } from '../reportGeneration/waterScreeningDisclosure';
import { applyPhaseStaffingDisclosure } from '../reportGeneration/phaseStaffingDisclosure';
/**
 * J3 — post-generation audit pipeline (read-only compose + mutating gate apply).
 *
 * Post-gen order (short IDs):
 *   E2 → G2 → G1 → I1 → H1 → H2 → H3 → I3 → J1 → J2 → M1
 */
import type { ReportTypeId } from '../../config/reportTypes';
import { AUDIT_CONFIG } from '../../config/auditConfig';
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteDocument, SiteRagReport } from '../../types/siteRag';
import {
  expandShortIds,
  expandShortOrder,
  POST_GEN_SHORT_ORDER,
  type AuditDetectorId,
  type DetectorRunStatus,
  type ShortDetectorId,
} from './auditDetectorRegistry';
import { applyActionDedupGate, clusterActions, extractActions, type ActionEmbedder } from './actionDeduper';
import { applyCaveatHonestyGate, verifyCaveatHonesty } from './caveatHonestyVerifier';
import {
  buildCitationEvidenceCache,
  verifyCitationIntegrity,
  applyCitationIntegrityGate,
  shouldRegenerateForCitations,
  type CitationViolation,
} from './citationIntegrityVerifier';
import { evaluateCitationDensityFinding } from './citationDensityGate';
import { applyConstraintElevationGate, detectElevationGap, extractConstraints } from './constraintExtractor';
import { checkDistributionSanity, getDistributionCheckMeta } from './distributionSanityChecker';
import {
  applyDocProvenanceGate,
  buildDocProvenanceEvidence,
  verifyDocProvenance,
} from './docProvenanceTracker';
import { applyInternalNumberGate, reconcileInternalNumbers, siteFloodZoneSet } from './internalNumberReconciler';
import { getBaselineForReportType } from './loadDistributionBaseline';
import {
  applyProviderPrecedenceGate,
  buildEvidenceCacheFromSed,
  verifyProviderPrecedence,
} from './providerPrecedenceVerifier';
import { applyProviderRelevanceGate, verifyProviderRelevance } from './providerRelevanceVerifier';
import {
  applyAbsenceAssertionGate,
  applyProjectStatusDiscipline,
  auditAbsenceAssertions,
  buildIntentDeclarationsForJudge,
} from './absenceAssertionDetector';
import { auditAiVoice } from './antiAiVoiceDetector';
import { applySectionDriftGate, verifySectionDrift, type SectionDriftFinding } from './sectionDriftVerifier';
import type { FullAuditFinding } from './runFullAudit';
import type { ModelFallbackSummary } from '../../config/geminiCapacityFallback';
import { logAuditEvent } from '../telemetry/auditTelemetry';
import { applySsmBodyGuardToReport } from '../ssmBodyGuard';
import { applyWaterUseScreeningGuard } from './waterUseScreeningGuard';
import { applySourceRoleTruthGuard } from './sourceRoleTruthGuard';
import { reconcileWholeParcelFloodZoneNarrative } from './floodAreaNarrativeReconciler';

export const POST_GEN_PIPELINE_ORDER: readonly AuditDetectorId[] = [
  ...expandShortOrder(POST_GEN_SHORT_ORDER),
];

export interface ComposePostGenAuditOptions {
  useLlmJudge?: boolean;
  embedFn?: (text: string) => Promise<number[]>;
  actionEmbedder?: ActionEmbedder;
  checkUrls?: boolean;
  auditOverrides?: import('../../types/site').SiteAuditOverride[];
  /**
   * R-131-EPISTEMIC-GROUNDING-B — inject a deterministic judge for tests.
   *
   * This module previously called `auditAbsenceAssertions` directly with no
   * seam at all, so a BLOCKING detector could only be stubbed via `vi.mock`.
   * Unlike `runFullAudit`'s `fixtureMode`, absence of a stub here means the
   * REAL judge runs — this is the production post-generation path and must
   * not quietly stop enforcing.
   */
  absenceLlmFn?: import('./absenceAssertionDetector').AbsenceAssertionLlmFn;
}

export interface ComposePostGenAuditResult {
  findings: FullAuditFinding[];
  detectorStatus: Partial<Record<AuditDetectorId, DetectorRunStatus>>;
  /** Why each `'failed'` detector failed. See FullAuditResult.detectorErrors. */
  detectorErrors?: Partial<Record<AuditDetectorId, string>>;
  /**
   * TEL-02 — absence-judge calls this READ-ONLY audit served from the 2.5
   * fallback (auditAbsenceAssertions collects them). Surfaced for the caller
   * (publish gate) and never stamped on the report: this path does not write
   * the report. Present only when at least one fallback happened.
   */
  absenceModelFallbacks?: ModelFallbackSummary;
}

export interface ApplyPostGenAuditGateContext {
  /** Enable only when the caller saves the corrected narrative with QA. */
  correctReliabilityMarkers?: boolean;
  sed: SiteEvidenceDocument | null | undefined;
  documents: SiteDocument[];
  siteId: string;
  reportId: string;
  reportType: string;
  useLlmJudge?: boolean;
  /** Only generation opts into convergence; audit/nightly callers retain one pass. */
  caveatNormalizePasses?: number;
  auditOverrides?: import('../../types/site').SiteAuditOverride[];
  /**
   * LIVE project intent at generation time (Codex review on #134): the R-78
   * gate stamps `qaFindings.providerPrecedenceViolations` consumed raw by
   * pillarScorer / dataConfidence / Path-to-GO / DOCX export — without the
   * live intent it grades against the SED's at-compile copy, so a voltage
   * the user just declared stays stamped as a block until the next SED
   * rebuild.
   */
  projectIntent?: import('../../types/siteRag').ProjectIntent | null;
  /** R-131-EPISTEMIC-GROUNDING-B — deterministic judge for tests; real judge when absent. */
  absenceLlmFn?: import('./absenceAssertionDetector').AbsenceAssertionLlmFn;
  /** When set and AUTO_REGENERATE_ON_BROKEN_CITES is on, may return a replacement report. */
  onCitationRetry?: (
    report: SiteRagReport,
    violations: CitationViolation[],
    evidence: ReturnType<typeof buildCitationEvidenceCache>,
  ) => Promise<SiteRagReport | null>;
}

function sectionDriftDetectorId(f: SectionDriftFinding): AuditDetectorId {
  if (f.detector === 'keyword') return 'R-62';
  if (f.detector === 'llm-judge') return 'R-63';
  return 'R-64';
}

function mockActionEmbedder(): ActionEmbedder {
  const dim = 16;
  return async (text: string) => {
    const v = new Array(dim).fill(0);
    for (let i = 0; i < text.length; i += 1) v[i % dim] += text.charCodeAt(i) / 1000;
    const norm = Math.sqrt(v.reduce((s, x) => s + x * x, 0)) || 1;
    return v.map((x) => x / norm);
  };
}

async function runPostGenStep(
  detectorIds: AuditDetectorId | AuditDetectorId[],
  status: Partial<Record<AuditDetectorId, DetectorRunStatus>>,
  errors: Partial<Record<AuditDetectorId, string>>,
  fn: () => Promise<void> | void,
): Promise<void> {
  const ids = Array.isArray(detectorIds) ? detectorIds : [detectorIds];
  try {
    await fn();
    for (const id of ids) {
      if (status[id] !== 'skipped') status[id] = 'ok';
    }
  } catch (e) {
    // Swallowing keeps one broken detector from killing the audit, but the
    // message must survive — a crashed detector emits zero findings and is
    // otherwise indistinguishable from a clean pass.
    const message = (e as Error)?.message ?? String(e);
    for (const id of ids) {
      status[id] = 'failed';
      errors[id] = message;
    }
  }
}

export function assertRequiredDetectorsInvoked(status: Partial<Record<AuditDetectorId, DetectorRunStatus>>): void {
  const required = expandShortIds(AUDIT_CONFIG.DETECTORS_REQUIRED as readonly ShortDetectorId[]);
  const missing = required.filter((id) => status[id] !== 'ok' && status[id] !== 'failed');
  if (missing.length > 0) {
    throw new Error(
      `composePostGenAudit: required detectors not invoked: ${missing.join(', ')}`,
    );
  }
}

/** Read-only post-gen audit — mirrors production detector set for parity tests. */
export async function composePostGenAudit(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  site: SiteEntity,
  opts: ComposePostGenAuditOptions = {},
): Promise<ComposePostGenAuditResult> {
  const useLlmJudge = opts.useLlmJudge ?? AUDIT_CONFIG.ENABLE_LLM_JUDGE;
  const embedFn = opts.embedFn ?? (async () => [] as number[]);
  const actionEmbedder = opts.actionEmbedder ?? mockActionEmbedder();
  const checkUrls = opts.checkUrls ?? AUDIT_CONFIG.CHECK_URLS;
  const reportTypeId = (report.reportType ?? 'feasibility') as ReportTypeId;

  const findings: FullAuditFinding[] = [];
  const detectorStatus: Partial<Record<AuditDetectorId, DetectorRunStatus>> = {};
  const detectorErrors: Partial<Record<AuditDetectorId, string>> = {};
  let absenceModelFallbacks: ModelFallbackSummary | undefined;
  const precedenceEvidence = buildEvidenceCacheFromSed(sed, { intent: site.projectIntent });

  // E2 / R-78
  await runPostGenStep('R-78', detectorStatus, detectorErrors, async () => {
    for (const v of verifyProviderPrecedence(report, precedenceEvidence)) {
      findings.push({
        detectorId: 'R-78',
        severity: v.severity,
        kind: v.fact,
        message: v.suggestedFix,
      });
    }
  });

  // G2 / R-67
  await runPostGenStep('R-67', detectorStatus, detectorErrors, async () => {
    // Same SED zone set as the stamped R-67 gate (applyInternalNumberGate).
    for (const f of reconcileInternalNumbers(report, { siteFloodZones: siteFloodZoneSet(sed) })) {
      findings.push({
        detectorId: 'R-67',
        severity: f.severity,
        kind: f.canonicalKey,
        message: f.label,
      });
    }
  });

  // G1 / R-66
  await runPostGenStep('R-66', detectorStatus, detectorErrors, async () => {
    const citationEvidence = buildCitationEvidenceCache(sed);
    const violations = await verifyCitationIntegrity(report, citationEvidence, { checkUrls });
    for (const v of violations) {
      findings.push({
        detectorId: 'R-66',
        severity: v.severity,
        kind: v.kind,
        message: v.message,
      });
    }
  });

  // R-3 — citation density (Part C SP3 / GAP-1). Same helper + args as
  // runFullAudit so the warn finding is byte-identical (pipeline parity).
  await runPostGenStep('R-3', detectorStatus, detectorErrors, () => {
    const densityFinding = evaluateCitationDensityFinding(report, site, reportTypeId);
    if (densityFinding) findings.push(densityFinding);
  });

  // I1 / R-70
  await runPostGenStep('R-70', detectorStatus, detectorErrors, async () => {
    const docEvidence = buildDocProvenanceEvidence(
      site.documents ?? [],
      sed,
    );
    for (const f of verifyDocProvenance(report, docEvidence)) {
      findings.push({
        detectorId: 'R-70',
        severity: f.severity,
        kind: f.kind,
        message: f.message,
      });
    }
  });

  // H1 / R-62/63/64
  await runPostGenStep(['R-62', 'R-63', 'R-64'], detectorStatus, detectorErrors, async () => {
    const driftFindings = await verifySectionDrift(report, { useLlmJudge, embedFn });
    const driftInvoked = new Set<AuditDetectorId>();
    for (const f of driftFindings) {
      const detId = sectionDriftDetectorId(f);
      driftInvoked.add(detId);
      findings.push({
        detectorId: detId,
        severity: f.severity,
        kind: `${f.detector}:${f.sectionId}`,
        type: f.detector,
        message: f.message,
      });
    }
    if (!useLlmJudge) {
      detectorStatus['R-63'] = 'skipped';
    } else if (!driftInvoked.has('R-63')) {
      detectorStatus['R-63'] = 'ok';
    }
    if (!driftInvoked.has('R-62')) detectorStatus['R-62'] = detectorStatus['R-62'] ?? 'ok';
    if (!driftInvoked.has('R-64')) detectorStatus['R-64'] = detectorStatus['R-64'] ?? 'ok';
  });

  // H2 / R-68
  await runPostGenStep('R-68', detectorStatus, detectorErrors, async () => {
    const constraints = extractConstraints(report, precedenceEvidence);
    for (const g of detectElevationGap(constraints, report)) {
      findings.push({
        detectorId: 'R-68',
        severity: g.severity,
        kind: g.kind,
        type: g.constraint.type,
        message: g.message,
      });
    }
  });

  // H3 / R-69
  await runPostGenStep('R-69', detectorStatus, detectorErrors, async () => {
    const actions = await extractActions(report, { skipLlmEnrichment: true });
    if (actions.length >= 2) {
      const clusters = await clusterActions(actions, actionEmbedder, AUDIT_CONFIG.ACTION_DEDUP_THRESHOLD);
      if (clusters.length < actions.length) {
        findings.push({
          detectorId: 'R-69',
          severity: 'warn',
          kind: 'DUPLICATE_ACTIONS',
          message: `${actions.length - clusters.length} near-duplicate action(s) detected`,
        });
      }
    }
  });

  // I3 / R-72
  await runPostGenStep('R-72', detectorStatus, detectorErrors, async () => {
    const baseline = getBaselineForReportType(reportTypeId);
    for (const f of checkDistributionSanity(report, baseline)) {
      findings.push({
        detectorId: 'R-72',
        severity: f.severity,
        kind: f.stat,
        message: f.message,
      });
    }
  });

  // J1 / R-75
  await runPostGenStep('R-75', detectorStatus, detectorErrors, async () => {
    const citationEvidence = buildCitationEvidenceCache(sed);
    for (const f of verifyCaveatHonesty(report, citationEvidence)) {
      findings.push({
        detectorId: 'R-75',
        severity: f.severity,
        kind: `band_gap_${f.bandGap}`,
        type: `${f.declaredBand}_vs_${f.actualBand}`,
        message: f.message,
      });
    }
  });

  // J2 / R-77
  await runPostGenStep('R-77', detectorStatus, detectorErrors, async () => {
    for (const f of verifyProviderRelevance(report, sed)) {
      if (f.severity === 'info') continue;
      findings.push({
        detectorId: 'R-77',
        severity: f.severity,
        kind: f.kind,
        type: f.providerId,
        message: f.message,
      });
    }
  });

  // M1 / R-131-EPISTEMIC-GROUNDING-B
  await runPostGenStep('R-131-EPISTEMIC-GROUNDING-B', detectorStatus, detectorErrors, async () => {
    // Run against the same truth-preserving status normalization used by the
    // mutating generation gate so the read-only audit and shipped body agree.
    const statusRewrite = applyProjectStatusDiscipline(report, site.projectIntent);
    const absenceAudit = await auditAbsenceAssertions(statusRewrite.report, {
      sed,
      siteId: site.id,
      reportId: report.id,
      reportType: report.reportType,
      intentDeclarations: buildIntentDeclarationsForJudge(site.projectIntent, sed ? sed.siteContext?.text : site.siteContext?.text),
      ...(opts.absenceLlmFn ? { llm: opts.absenceLlmFn } : {}),
    });
    absenceModelFallbacks = absenceAudit.modelFallbacks;
    for (const f of absenceAudit.findings) {
      findings.push({
        detectorId: 'R-131-EPISTEMIC-GROUNDING-B',
        severity: 'block',
        kind: f.suspectedClass,
        type: f.sectionId,
        message: f.message,
      });
    }
  });

  // N1 / R-132-ANTI-AI-VOICE
  await runPostGenStep('R-132-ANTI-AI-VOICE', detectorStatus, detectorErrors, async () => {
    for (const f of auditAiVoice(report)) {
      findings.push({
        detectorId: 'R-132-ANTI-AI-VOICE',
        severity: f.severity,
        kind: 'AI_VOICE',
        type: f.sectionId,
        message: f.message,
      });
    }
  });

  assertRequiredDetectorsInvoked(detectorStatus);

  return {
    findings,
    detectorStatus,
    detectorErrors,
    ...(absenceModelFallbacks ? { absenceModelFallbacks } : {}),
  };
}

/** Mutating post-gen gates — shared by legacy and typed generation paths. */
export async function applyPostGenAuditGates(
  report: SiteRagReport,
  ctx: ApplyPostGenAuditGateContext,
): Promise<SiteRagReport> {
  // FEMA-AREA-1 — the provider keeps one highest-risk/query-point zone for
  // backwards compatibility, but an exact parcel-polygon area analysis is the
  // authority for whole-site prose. Correct every reader-visible projection
  // before any detector snapshots the generated body. Evidence/source claims
  // are explicitly excluded by the reconciler.
  const floodReconciliation = reconcileWholeParcelFloodZoneNarrative(
    report,
    ctx.sed?.flood?.data?.areaAnalysis,
  );
  let out = reconcilePhaseTimelineClaims(applyPhaseStaffingDisclosure(applyWaterScreeningDisclosure(floodReconciliation.report, ctx.sed, ctx.reportType, ctx.projectIntent), ctx.sed, ctx.projectIntent, ctx.reportType), ctx.projectIntent);
  if (ctx.correctReliabilityMarkers === true) {
    out = applyReliabilityMarkerCorrection(out, { stage: 'post-generation', sed: ctx.sed });
  }
  if (floodReconciliation.correctedCount > 0) {
    console.info(
      `[FEMA-AREA-1] corrected ${floodReconciliation.correctedCount} whole-parcel flood-zone narrative projection(s)`,
    );
  }
  const useLlmJudge = ctx.useLlmJudge ?? AUDIT_CONFIG.ENABLE_LLM_JUDGE;
  const reportTypeId = (out.reportType ?? 'feasibility') as ReportTypeId;

  // R-78 — Provider-precedence is DETECT-AND-RECORD-ONLY (never a publish gate).
  // The gate stamps any mismatches onto `qaFindings.providerPrecedenceViolations`
  // and NEVER throws/blocks; the report persists normally and the AUDIT section
  // (SiteAuditTab via runUserAudit) surfaces the mismatches review-only. Severity
  // tags are preserved for the audit display.
  try {
    out = applyProviderPrecedenceGate(out, ctx.sed, {
      autocorrect: AUDIT_CONFIG.AUTOCORRECT_PRECEDENCE,
      intent: ctx.projectIntent,
    });
  } catch (e) {
    console.warn('[R-78 precedence gate failed]', (e as Error)?.message);
  }

  // R-67
  try {
    out = applyInternalNumberGate(out, ctx.sed, {
      autocorrect: AUDIT_CONFIG.AUTOCORRECT_INTERNAL_NUMBERS,
      auditOverrides: ctx.auditOverrides,
    });
  } catch (e) {
    console.warn('[R-67 internal number gate failed]', (e as Error)?.message);
  }

  // R-66
  try {
    const evidence = buildCitationEvidenceCache(ctx.sed);
    const violations = await verifyCitationIntegrity(out, evidence, {
      checkUrls: AUDIT_CONFIG.CHECK_URLS,
    });
    if (
      ctx.onCitationRetry
      && shouldRegenerateForCitations(violations)
      && AUDIT_CONFIG.AUTO_REGENERATE_ON_BROKEN_CITES
    ) {
      const retried = await ctx.onCitationRetry(out, violations, evidence);
      if (retried) return retried;
    }
    // Provenance-gap [unverified] downgrade is disabled by default (product
    // decision) — keep the original [Ref N] citations. Re-enable via
    // AUDIT_CITATION_PROVENANCE_DOWNGRADE=true. The verify pass above still runs
    // (drives the optional regenerate-on-broken-cites retry).
    if (AUDIT_CONFIG.APPLY_CITATION_PROVENANCE_DOWNGRADE) {
      out = applyCitationIntegrityGate(out, violations);
    }
  } catch (e) {
    console.warn('[R-66 citation gate failed]', (e as Error)?.message);
  }

  // H-3 — SSM body guard (DEFAULT OFF, gated on `SSM_BODY_GUARD`).
  //
  // Complements R-66: where R-66 downgrades refs that FAIL the provenance
  // gate, the SSM guard flags the narrower "inferred-from-context" class
  // (no provider URL + an inference sentinel in the source string) that can
  // otherwise pass R-66's heuristic. Only fires when the report is in
  // `degradedMode` AND the flag is on AND a cited ref is inference-only — it
  // prepends an `[unverified inferred-from-context]` marker to the inline
  // `[Ref N]` token (rendered as the existing rose "unverified" badge). Never
  // fabricates, hides, or removes a citation. With the flag OFF this is a
  // byte-identical no-op (returns the input report by identity).
  try {
    out = applySsmBodyGuardToReport(out);
  } catch (e) {
    console.warn('[H-3 SSM body guard failed]', (e as Error)?.message);
  }

  // R-70
  try {
    const evidence = buildDocProvenanceEvidence(ctx.documents ?? [], ctx.sed);
    out = applyDocProvenanceGate(out, evidence);
  } catch (e) {
    console.warn('[R-70 doc provenance gate failed]', (e as Error)?.message);
  }

  // R-62/R-63/R-64
  try {
    out = await applySectionDriftGate(out, {
      useLlmJudge,
      siteId: ctx.siteId,
      reportId: ctx.reportId,
    });
  } catch (e) {
    console.warn('[R-62/R-64 section drift gate failed]', (e as Error)?.message);
  }

  // R-68
  try {
    out = applyConstraintElevationGate(out, ctx.sed, {
      autoElevate: AUDIT_CONFIG.AUTO_ELEVATE_CONSTRAINTS,
    });
  } catch (e) {
    console.warn('[R-68 constraint elevation gate failed]', (e as Error)?.message);
  }

  // R-69
  try {
    out = await applyActionDedupGate(out, {
      autoDedup: AUDIT_CONFIG.AUTO_DEDUP_ACTIONS,
      minReductionRatio: 0.15,
    });
  } catch (e) {
    console.warn('[R-69 action dedup gate failed]', (e as Error)?.message);
  }

  // R-72
  try {
    const baseline = getBaselineForReportType(reportTypeId);
    const distributionFindings = checkDistributionSanity(out, baseline);
    const distributionCheckMeta = getDistributionCheckMeta(reportTypeId, baseline);
    out = {
      ...out,
      qaFindings: {
        ...(out.qaFindings ?? {}),
        distribution: distributionFindings,
        distributionCheckMeta,
      },
    };
    for (const f of distributionFindings) {
      logAuditEvent({
        type: 'distribution-outlier',
        reportId: out.id,
        reportType: reportTypeId,
        siteId: ctx.siteId,
        payload: {
          stat: f.stat,
          observed: f.observed,
          severity: f.severity,
          p10: f.p10,
          p90: f.p90,
        },
      });
    }
  } catch (e) {
    console.warn('[R-72 distribution sanity gate failed]', (e as Error)?.message);
  }

  // R-75
  try {
    const evidence = buildCitationEvidenceCache(ctx.sed);
    const caveatFindings = verifyCaveatHonesty(out, evidence);
    for (const f of caveatFindings) {
      logAuditEvent({
        type: 'caveat-honesty-violation',
        reportId: out.id,
        reportType: ctx.reportType,
        payload: {
          severity: f.severity,
          declaredBand: f.declaredBand,
          actualBand: f.actualBand,
          bandGap: f.bandGap,
          claimId: f.claimId,
        },
      });
    }
    // TRUTHFUL DOWNGRADE (2026-08-14): blocks are exactly what autocorrect
    // exists to fix — the gate rewrites the over-claiming language to the
    // evidence band and re-verifies; only unrewritable claims still hold.
    out = applyCaveatHonestyGate(out, ctx.sed, {
      autocorrect: AUDIT_CONFIG.AUTOCORRECT_CAVEATS,
      // A tier-3-only BLOCK with a grammar-safe downgrade is repaired at the
      // generation boundary and then re-verified. Mixed/unknown evidence and
      // unrewritable prose still fail closed; broad caveat rewriting remains
      // behind AUDIT_AUTOCORRECT_CAVEATS.
      normalizeTier3Blocks: true,
      maxNormalizePasses: ctx.caveatNormalizePasses,
      evidence,
      findings: caveatFindings,
    });
  } catch (e) {
    console.warn('[R-75 caveat honesty gate failed]', (e as Error)?.message);
  }

  // R-77
  try {
    out = applyProviderRelevanceGate(out, ctx.sed, {
      reportId: ctx.reportId,
      siteId: ctx.siteId,
    });
    for (const f of out.qaFindings?.providerRelevance ?? []) {
      if (f.severity === 'info') continue;
      logAuditEvent({
        type: 'provider-relevance-violation',
        reportId: out.id,
        reportType: ctx.reportType,
        siteId: ctx.siteId,
        payload: {
          severity: f.severity,
          kind: f.kind,
          sectionId: f.sectionId,
          providerId: f.providerId,
          refIndex: f.refIndex,
        },
      });
    }
  } catch (e) {
    console.warn('[R-77 provider relevance gate failed]', (e as Error)?.message);
  }

  // WATER-TRUTH-1 — for every report that receives economic water evidence,
  // compare narrative quantities against the deterministic SED water screen.
  // This runs after prose-mutating gates so its QA snapshot describes the
  // final reader-visible claims.
  // This detector is a publish boundary, not advisory enrichment. Let an
  // unexpected detector failure fail the audit rather than silently publish a
  // report whose water claims were never checked.
  out = applyWaterUseScreeningGuard(out, ctx.sed, ctx.reportType);

  // R-131-EPISTEMIC-GROUNDING-B
  try {
    const contractStatusRewrite = applyProjectStatusDiscipline(out, ctx.projectIntent);
    out = contractStatusRewrite.report;
    const absenceAudit = await auditAbsenceAssertions(out, {
      sed: ctx.sed,
      siteId: ctx.siteId,
      reportId: ctx.reportId,
      reportType: ctx.reportType,
      intentDeclarations: buildIntentDeclarationsForJudge(ctx.projectIntent, ctx.sed?.siteContext?.text),
      ...(ctx.absenceLlmFn ? { llm: ctx.absenceLlmFn } : {}),
    });
    if (contractStatusRewrite.applied > 0) {
      absenceAudit.autocorrectApplied = true;
      absenceAudit.rewritesApplied = contractStatusRewrite.applied;
    }
    out = applyAbsenceAssertionGate(out, absenceAudit);
    for (const f of absenceAudit.findings) {
      logAuditEvent({
        type: 'absence-assertion-violation',
        reportId: out.id,
        reportType: ctx.reportType,
        siteId: ctx.siteId,
        payload: {
          severity: 'block',
          sectionId: f.sectionId,
          suspectedClass: f.suspectedClass,
          sentenceText: f.sentenceText.slice(0, 200),
        },
      });
    }
  } catch (e) {
    console.warn('[R-131-EPISTEMIC-GROUNDING-B absence gate failed]', (e as Error)?.message);
  }

  return applySourceRoleTruthGuard(out, ctx.sed);
}

export { PARITY_ALLOWLIST } from './auditDetectorRegistry';
